import {t} from "@lingui/core/macro";
import * as React from "react";
import {useContext, useState} from "react";

import {Text, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import Pill from "@khanacademy/wonder-blocks-pill";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {SimpleTable, type Column} from "@khan/component-table";
import {doNotTranslate, doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation} from "@khan/urls";

import {ColumnName} from "./column-names.ts";
import {EmptyState} from "./empty-state.tsx";
import type {guideTranscripts_aiGuideThreads_threads as ThreadType} from "./guide-transcripts/__graphql-types__/guideTranscripts.ts";
import {GuideTranscriptModal} from "./guide-transcripts/guide-transcript-modal.tsx";
import {countNumberOfInteractions, formatPageInfoString} from "./utils.ts";

import chatCircleIcons from "@phosphor-icons/core/regular/chat-circle.svg";

interface Props {
    threads: Array<ThreadType>;
    /**
     * The relationship between the viewer and the profile owner. Used for
     * empty state messaging.
     */
    relationship: "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";
    /**
     * For the conversations view, if a conversation is flagged, render the
     * notice in the first column, under the Chat Name.
     */
    showFlaggedNotice?: boolean;
    /**
     * The columns to display in the table. Defaults to all columns.
     * The values are the keys of the ThreadType object. Order matters.
     */
    visibleColumns?: Array<ColumnName>;
    /**
     * Hide the resume thread link in the chat modal.
     */
    hideResumeThreadLink?: boolean;
    /**
     * Whether filters are currently applied (non-default values).
     * Used to determine which empty state message to show.
     */
    hasAppliedFilters?: boolean;
}

/**
 * Renders a table view of chat threads.
 *
 * @param props - The component props
 * @param props.threads - The list of chat threads to display in the table
 * @param props.showFlaggedNotice - Whether to display a notice for flagged threads (defaults to false)
 * @param props.visibleColumns - The columns to display in the table (defaults to all columns)
 * @param props.hideResumeThreadLink - Hide the resume thread link in the chat modal
 * @param props.relationship - The relationship between the viewer and the profile owner. Used for empty state messaging.
 * @returns A React element containing either a table of threads or an empty state message
 */
export default function TableView({
    threads,
    showFlaggedNotice = false,
    visibleColumns = [
        ColumnName.chatName,
        ColumnName.chatSource,
        ColumnName.flagged,
        ColumnName.messages,
        ColumnName.date,
    ],
    hideResumeThreadLink,
    relationship,
    hasAppliedFilters = false,
}: Props): React.ReactElement {
    const {entrypoint} = useContext(AppShellContext);
    const isClassroom = entrypoint === "classroom";
    const {pathname} = useLocation();
    const isTutorMe = pathname.includes("/tutor-me");
    const shouldSuppressCTA = isClassroom && isTutorMe;
    const ctaHref = isClassroom ? "/learner/tutor-me" : "learner/class";

    // State to control which modal is open (using thread ID)
    const [openModalThreadId, setOpenModalThreadId] = useState<string | null>(
        null,
    );

    const handleOpenModal = (threadId: string) => {
        setOpenModalThreadId(threadId);
    };

    // Show different empty states based on whether filters are applied
    if (threads.length === 0) {
        if (hasAppliedFilters) {
            // When filters are applied but no results found
            return (
                <EmptyState
                    title={t`No results found.`}
                    subtitle={t`Your search didn't match any results. Please try to change the filters.`}
                />
            );
        }

        if (relationship === "SELF") {
            // When no filters are applied and no chats exist (default state)
            return (
                <EmptyState
                    title={t`No chats yet!`}
                    subtitle={t`Once you get started, your chats will show up here.`}
                    href={shouldSuppressCTA ? undefined : ctaHref}
                    buttonText={t`Start your first chat`}
                    showButton={!shouldSuppressCTA}
                />
            );
        }

        // For non-self relationships when no filters are applied
        return (
            <EmptyState
                title={t`All chats have been filtered out`}
                subtitle={t`Change the filters to display your student's progress.`}
            />
        );
    }

    return (
        <View>
            <SimpleTable
                columns={getColumns(
                    showFlaggedNotice,
                    visibleColumns,
                    hideResumeThreadLink,
                    handleOpenModal,
                )}
                data={threads}
                defaultSortKey="lastUpdatedAt"
                // We're using custom pagination which changes the url
                paginationAfterTable={false}
            />
            {/* Render the modal using ModalLauncher in controlled mode */}
            <ModalLauncher
                opened={openModalThreadId !== null}
                onClose={() => setOpenModalThreadId(null)}
                modal={({closeModal}) => (
                    <GuideTranscriptModal
                        transcriptId={openModalThreadId || ""}
                        relationship="SELF"
                        closeModal={closeModal}
                        hideResumeThreadLink={hideResumeThreadLink}
                        systemChatStyle={{
                            backgroundColor: "none",
                        }}
                        userChatStyle={{
                            backgroundColor: "none",
                            border: "1px solid #CBCBCD",
                            color: color.offBlack,
                        }}
                    />
                )}
            />
        </View>
    );
}

/**
 * Generates the columns configuration for a chat history table.
 *
 * The function creates an array of column definitions for displaying chat thread information.
 * Each column contains properties such as label, data key, sort options, and cell render functions.
 *
 * @param showFlaggedNotices - Whether to show flagged notices within the chat name column
 * @param visibleColumns - The columns to display in the table
 * @param hideResumeThreadLink - Hide the resume thread link in the chat modal
 * @param onOpenModal - Handler to open the modal for a specific thread
 *
 * @returns An array of Column objects configured for the ThreadType data
 *
 * The columns include:
 * - Previous chats (with optional flagged notice)
 * - Source
 * - Flagged status
 * - Messages count
 * - Date (last updated)
 * - Preview action button
 */
function getColumns(
    showFlaggedNotices: boolean,
    visibleColumns: Array<ColumnName>,
    hideResumeThreadLink?: boolean,
    onOpenModal?: (threadId: string) => void,
): Array<Column<ThreadType>> {
    const allColumns: Record<ColumnName, Column<ThreadType>> = {
        chatName: {
            label: doNotTranslate`Previous chats`,
            dataKey: "title",
            sortable: "ascending",
            cell: (row: ThreadType) => {
                const date = new Date(row.lastUpdatedAt).toLocaleString(
                    RequestInfo.kaLocale,
                );
                const title = row.title || t`Khanmigo chat from ${date}`;
                return (
                    <>
                        <Tooltip
                            content={
                                row.summary ||
                                doNotTranslateYet`Summary coming soon.`
                            }
                        >
                            <Link
                                onClick={(e) => {
                                    e.preventDefault();
                                    onOpenModal?.(row.id);
                                }}
                                href={`${row.id}`}
                            >
                                {title}
                            </Link>
                        </Tooltip>
                        {row.flagged && showFlaggedNotices && (
                            // TODO(davemcalpine): replace this with a Badge
                            // component when ready. Badge Epic:
                            // https://khanacademy.atlassian.net/browse/WB-1934
                            <>
                                <br />
                                <Pill
                                    kind="warning"
                                    style={{
                                        marginBlockStart: spacing.xSmall_8,
                                    }}
                                >
                                    {t`This chat has been flagged by Khan Academy's moderation system`}
                                </Pill>
                            </>
                        )}
                    </>
                );
            },
        },
        chatSource: {
            label: doNotTranslate`Source`,
            cell: (row: ThreadType) => {
                return formatPageInfoString(row);
            },
        },
        flagged: {
            label: doNotTranslate`Flagged`,
            dataKey: "flagged",
            cell: (row: ThreadType) => {
                return row.flagged ? (
                    // TODO(davemcalpine): replace this with a Badge
                    // component when ready. Badge Epic:
                    // https://khanacademy.atlassian.net/browse/WB-1934
                    <Pill kind="critical">{t`Flagged`}</Pill>
                ) : (
                    <Text>&mdash;</Text>
                );
            },
        },
        messages: {
            label: doNotTranslate`Messages`,
            // NOTE: this is not yet sortable because the number of messages
            // is a computed value, not a field in the data.
            // See call to `countNumberOfInteractions` below.
            cell: (row: ThreadType) => {
                return (
                    <View style={{alignItems: "center", flexDirection: "row"}}>
                        <PhosphorIcon icon={chatCircleIcons} />
                        <Strut size={spacing.xxxSmall_4} />
                        {countNumberOfInteractions(row)}
                    </View>
                );
            },
        },
        date: {
            label: doNotTranslate`Date`,
            dataKey: "lastUpdatedAt",
            sortable: "descending",
            cell: (row: ThreadType) => {
                return new Date(row.lastUpdatedAt).toLocaleString(
                    RequestInfo.kaLocale,
                );
            },
        },
    };

    const columns: Array<Column<ThreadType>> = [];

    visibleColumns.forEach((column) => {
        if (allColumns[column]) {
            columns.push(allColumns[column]);
        }
    });

    return columns;
}
